import React, { useRef, useEffect, useMemo, useState } from "react";
import {
	Button,
	IconButton,
	ListItem,
	ListItemText,
	ListItemButton,
	Box,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Tooltip,
	Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { lprService, associationService } from "client-app-core";
import { useSelector, useDispatch } from "react-redux";
import { openDialog, closeDialog } from "orion-components/AppState/Actions";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getDir } from "orion-components/i18n/Config/selector";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";
import { Translate, getTranslation } from "orion-components/i18n";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import { getWatchlists } from "../actions";

const baseStyles = {
	hotListUIControlsHeader: { margin: "0 13px 16px" },
	watchlistsListItem: {
		height: 52,
		backgroundColor: "#494D53",
		borderRadius: 5,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		flexShrink: 0,
		marginBottom: 10
	},
	textFieldInputLabelProps: {
		fontFamily: "Roboto",
		fontSize: "12px",
		color: "#aaa"
	},
	standardTextFieldInputProps: {
		sx: {
			fontFamily: "Roboto",
			fontWeight: 400,
			fontSize: "14px",
			lineHeight: "100%",
			letterSpacing: "0",
			textAlign: "center",
			color: "#FFF",
			"& input": {
				padding: "0px 0px 10px 0px"
			},
			"&:before": {
				borderBottom: "1px solid #949597"
			},
			"&:after": {
				borderBottom: "1px solid #42a5f5"
			},
			"&:hover:not(.Mui-disabled):before": {
				borderBottom: "1px solid #42a5f5"
			}
		},
		autoComplete: "off"
	},
	watchlistTextFieldSx: {
		"& .MuiSelect-select.MuiSelect-standard.MuiInputBase-input.MuiInput-input": {
			textAlign: "left !important"
		},
		"& .MuiSelect-icon": {
			color: "#fff"
		},
		input: {
			color: "#fff",
			fontFamily: "Roboto",
			fontSize: "12px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "normal"
		},
		label: {
			color: "rgba(255, 255, 255, 0.3)",
			fontFamily: "Roboto",
			fontSize: "12px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "normal"
		}
	},
	actionButtonsRow: { gap: "1rem" },
	primaryActionButton: {
		width: "150px",
		height: "29px",
		backgroundColor: "#4CB5F4",
		fontSize: "12px",
		fontWeight: 700,
		fontFamily: "Roboto",
		borderRadius: "5px",
		alignSelf: "center",
		color: "#FFFFFF",
		":hover": {
			backgroundColor: "#42a5f5"
		},
		textTransform: "uppercase"
	},
	hiddenFileInput: { display: "none" },
	uploadStatusMessage: uploadStatus => ({
		color: uploadStatus === "Success" ? "#4CAF50" : uploadStatus === "DuplicateEntries" ? "#FFC107" : "#f44336",
		textAlign: "center",
		fontSize: "12px",
		height: "25px"
	}),
	watchlistsScrollContainer: scrollBarsEnabled => ({
		overflowY: "scroll",
		marginLeft: 13,
		marginTop: 16,
		...(!scrollBarsEnabled ? { marginRight: 13 } : {})
	}),
	listItemHoverActions: {
		".MuiListItemSecondaryAction-root": {
			opacity: 0,
			visibility: "hidden",
			transition: "opacity 0.2s ease, visibility 0.2s ease",
			marginRight: "-8px"
		},
		"&:hover .MuiListItemSecondaryAction-root": {
			opacity: 1,
			visibility: "visible"
		}
	},
	secondaryActionContainer: { display: "flex", gap: "4px" },
	watchlistItemButtonSx: {
		paddingLeft: 0
	},
	whiteIconButton: { color: "#fff" },
	deletableIconButton: {
		color: "#fff",
		"&.Mui-disabled": {
			color: "#ffffff4d"
		}
	},
	emptyStateMessage: { padding: 20, textAlign: "center", color: "white" },
	editDialogPaperSx: {
		backgroundColor: "#41454a",
		padding: "20px",
		borderRadius: 0,
		width: "450px",
		height: "300px"
	},
	deleteDialogPaperSx: {
		backgroundColor: "#41454a",
		padding: "20px",
		borderRadius: 0,
		width: "350px",
		height: "225px"
	},
	whiteTypography: { color: "#fff" },
	editDialogFormBox: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: "1rem",
		padding: "12px"
	},
	dialogButton: { height: 45, color: "#fff" },
	saveButtonSx: {
		textTransform: "uppercase",
		color: "#fff",
		":hover": {
			backgroundColor: "#42a5f5"
		},
		"&.Mui-disabled": {
			color: "#ffffff4d"
		}
	},
	deleteConfirmButtonSx: {
		backgroundColor: "#f44336",
		textTransform: "uppercase",
		":hover": {
			backgroundColor: "#E95858"
		}
	},
	deleteWatchlistBackButton: canDeleteWatchlist => ({
		height: 45,
		color: canDeleteWatchlist ? "#fff" : "#42a5f5"
	})
};

const WatchlistsView = ({ onSelectWatchlist }) => {
	const dispatch = useDispatch();
	const textRefs = useRef({});

	const canManageLPR = useSelector(state => canManageByApplication(state, "lpr-app", "manage"));
	const dir = useSelector(state => getDir(state));
	const scrollBarsEnabled = useSelector(state => getScrollBarAppState(state));
	const dialog = useSelector(state => state.appState.dialog.openDialog);
	const watchlists = useSelector(state => state.appState.dock.dockData.watchlists);

	const styles = useMemo(() => generateDirStyles(baseStyles, dir), [dir]);

	const [uploadStatus, setUploadStatus] = useState("");
	const [uploadResult, setUploadResult] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState("");
	const [errorContext, setErrorContext] = useState("");
	const [newName, setNewName] = useState("");
	const [newDesc, setNewDesc] = useState("");
	const [updatedName, setUpdatedName] = useState("");
	const [updatedDesc, setUpdatedDesc] = useState("");
	const [currentWatchlist, setCurrentWatchlist] = useState(null);
	const [canDeleteWatchlist, setCanDeleteWatchlist] = useState(true);
	const [tooltipStates, setTooltipStates] = useState({});

	const sortedWatchlists = useMemo(
		() =>
			[...(watchlists || [])].sort((a, b) => {
				const nameA = a.name.toUpperCase();
				const nameB = b.name.toUpperCase();
				return nameA.localeCompare(nameB);
			}),
		[watchlists]
	);

	useEffect(() => {
		dispatch(getWatchlists());
	}, [dispatch]);

	useEffect(() => {
		// Clears the entry fields and any errors/status messages
		setNewName("");
		setNewDesc("");
		setError("");
		setErrorContext("");
		setUploadStatus("");
	}, []);

	const handleLPRUpload = event => {
		setUploadStatus("");
		const file = event.target.files[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = e => {
			try {
				const csvText = e.target.result;
				setUploading(true);
				lprService.addFromCSV(csvText, (err, result) => {
					if (err) {
						console.error("Error while adding entries from CSV", err);
						setUploadStatus("Failure");
						setUploading(false);
					} else {
						if (result.success === false) {
							setUploadStatus("Failure");
						} else if (result.duplicateEntries > 0) {
							setUploadStatus("DuplicateEntries");
							setUploadResult(result);
						} else {
							setUploadStatus("Success");
							setUploadResult(result);
						}
						setUploading(false);
						dispatch(getWatchlists());
					}
				});
			} catch (err) {
				console.error("Error while loading CSV", err);
				setUploadStatus("Failure");
				setUploading(false);
			}
		};
		reader.readAsText(file);
	};

	const addNewWatchlist = (name, description) => {
		if (!name || !name.trim()) {
			setError("emptyWatchlistName");
			setErrorContext("newWatchlistName");
			return;
		}
		setError("");
		setErrorContext("");
		lprService.addWatchlist(name.trim(), description.trim(), (err, res) => {
			if (err) {
				setError(err.reason);
				setErrorContext("newWatchlistName");
			} else {
				if (res.success === false) {
					setError(res.reason.message);
					setErrorContext("newWatchlistName");
				} else {
					setNewName("");
					setNewDesc("");
					dispatch(getWatchlists());
				}
			}
		});
	};

	const selectWatchlist = watchlist => {
		setCurrentWatchlist(watchlist);
		onSelectWatchlist(watchlist);
	};

	const openEditDialog = watchlist => {
		setCurrentWatchlist(watchlist);
		setUpdatedName(watchlist.name);
		setUpdatedDesc(watchlist.description);
		dispatch(openDialog("editWatchlist"));
	};

	const updateWatchlist = (id, newName, newDescription = "") => {
		if (!newName || !newName.trim()) {
			setError("emptyWatchlistName");
			setErrorContext("updatedWatchlistName");
			return;
		}
		setError("");
		setErrorContext("");
		lprService.updateWatchlist(id, newName.trim(), newDescription.trim(), (err, res) => {
			if (err) {
				setError(err.reason);
				setErrorContext("updatedWatchlistName");
			} else {
				if (res.success === false) {
					setError(res.reason.message);
					setErrorContext("updatedWatchlistName");
				} else {
					dispatch(closeDialog("editWatchlist"));
					dispatch(getWatchlists());
				}
			}
		});
	};

	const openDeleteWatchlistDialog = watchlist => {
		setCurrentWatchlist(watchlist);
		associationService.checkAssociations(watchlist.id, (err, result) => {
			if (err) {
				console.error(err);
			} else {
				const { hasAssociations } = result;
				setCanDeleteWatchlist(hasAssociations === 0);
			}
		});
		dispatch(openDialog("deleteWatchlist"));
	};

	const deleteWatchlist = id => {
		if (!id) {
			console.error("Missing ID for deletion");
			return;
		}
		lprService.deleteWatchlist(id, (err, res) => {
			if (err) {
				console.error(err.message, err);
			} else {
				dispatch(closeDialog("deleteWatchlist"));
				dispatch(getWatchlists());
			}
		});
	};

	useEffect(() => {
		// Check overflow for all text elements
		const checkAllOverflows = () => {
			const newTooltipStates = {};
			Object.entries(textRefs.current).forEach(([itemId, element]) => {
				if (element) {
					newTooltipStates[itemId] = element.scrollWidth > element.clientWidth;
				}
			});
			setTooltipStates(newTooltipStates);
		};

		checkAllOverflows();
		window.addEventListener("resize", checkAllOverflows);
		return () => window.removeEventListener("resize", checkAllOverflows);
	}, [sortedWatchlists]);

	return (
		<>
			{canManageLPR && (
				<>
					<div style={styles.hotListUIControlsHeader}>
						<TextField
							label={getTranslation("global.sharedComponents.LPRWatchlistPanel.nameEntry")}
							value={newName}
							error={errorContext === "newWatchlistName"}
							helperText={
								errorContext === "newWatchlistName" && error
									? getTranslation(`global.sharedComponents.LPRWatchlistPanel.error.${error}`)
									: ""
							}
							onChange={event => {
								setNewName(event.target.value);
								if (errorContext === "newWatchlistName") {
									setErrorContext("");
									setError("");
								}
							}}
							variant="standard"
							fullWidth
							InputProps={styles.standardTextFieldInputProps}
							InputLabelProps={{
								sx: styles.textFieldInputLabelProps
							}}
							sx={styles.watchlistTextFieldSx} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-text-field"
						/>
						<TextField
							label={getTranslation("global.sharedComponents.LPRWatchlistPanel.description")}
							value={newDesc}
							onChange={event => {
								setNewDesc(event.target.value);
							}}
							variant="standard"
							fullWidth
							InputProps={styles.standardTextFieldInputProps}
							InputLabelProps={{
								sx: styles.textFieldInputLabelProps
							}}
							sx={styles.watchlistTextFieldSx} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-text-field-2"
						/>
					</div>
					<Box
						display="flex"
						fullWidth
						justifyContent="center"
						maxHeight="40px"
						style={styles.actionButtonsRow}
					>
						<Button
							onClick={() => addNewWatchlist(newName, newDesc)}
							variant="contained"
							sx={styles.primaryActionButton} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-button"
						>
							<Translate value="global.sharedComponents.LPRWatchlistPanel.addWatchlist" />
						</Button>
						<Button
							component="label"
							variant="contained"
							loading={uploading}
							sx={styles.primaryActionButton} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-button-2"
						>
							<Translate value="global.sharedComponents.LPRWatchlistPanel.uploadWatchlistEntries" />
							<input
								type="file"
								multiple
								accept=".csv"
								style={styles.hiddenFileInput}
								onChange={handleLPRUpload} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-input"
							/>
						</Button>
					</Box>
					{uploadStatus && (
						<p style={styles.uploadStatusMessage(uploadStatus)}>
							{uploadStatus
								? getTranslation(
										`global.sharedComponents.LPRWatchlistPanel.upload${uploadStatus}`,
										uploadResult?.duplicateEntries
									)
								: ""}
						</p>
					)}
				</>
			)}
			<div style={styles.watchlistsScrollContainer(scrollBarsEnabled)}>
				{sortedWatchlists.length > 0 ? (
					sortedWatchlists.map(item => (
						<Tooltip
							key={item.id || item.name}
							title={item.name}
							disableFocusListener
							placement="bottom-start"
							disableHoverListener={!tooltipStates[item.id]}
							slotProps={{
								popper: {
									modifiers: [
										{
											name: "offset",
											options: {
												offset: [0, -14]
											}
										}
									]
								}
							}} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-tooltip"
						>
							<ListItem
								style={styles.watchlistsListItem}
								sx={canManageLPR ? styles.listItemHoverActions : undefined}
								secondaryAction={
									canManageLPR && (
										<div style={styles.secondaryActionContainer}>
											<IconButton
												style={styles.whiteIconButton}
												aria-label="edit"
												onClick={e => {
													e.stopPropagation();
													openEditDialog(item);
												}} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-icon-button"
											>
												<EditIcon />
											</IconButton>
											<IconButton
												sx={styles.deletableIconButton}
												aria-label="delete"
												onClick={e => {
													e.stopPropagation();
													openDeleteWatchlistDialog(item);
												}} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-icon-button-2"
											>
												<DeleteIcon />
											</IconButton>
										</div>
									)
								} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-list-item"
							>
								<ListItemButton sx={styles.watchlistItemButtonSx} onClick={() => selectWatchlist(item)} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-list-item-button">
									<ListItemText
										primary={item.name}
										primaryTypographyProps={{
											noWrap: true,
											ref: el => {
												if (el) {
													textRefs.current[item.id] = el;
												}
											}
										}}
									/>
								</ListItemButton>
							</ListItem>
						</Tooltip>
					))
				) : (
					<div style={styles.emptyStateMessage}>
						{getTranslation("global.sharedComponents.LPRWatchlistPanel.noWatchlists")}
					</div>
				)}
			</div>
			{canManageLPR && (
				<>
					<Dialog
						open={dialog === "editWatchlist"}
						PaperProps={{
							sx: styles.editDialogPaperSx
						}} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-dialog"
					>
						<DialogTitle data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-dialog-title">
							<Typography style={styles.whiteTypography}>
								{getTranslation("global.sharedComponents.LPRWatchlistPanel.dialog.editWatchlistTitle")}
							</Typography>
						</DialogTitle>
						<DialogContent data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-dialog-content">
							<Box
								fullWidth
								display="flex"
								flexDirection="column"
								alignItems="center"
								gap="1rem"
								padding="12px"
								style={styles.editDialogFormBox}
							>
								<TextField
									fullWidth
									variant="standard"
									label={getTranslation("global.sharedComponents.LPRWatchlistPanel.nameEntry")}
									value={updatedName}
									error={errorContext === "updatedWatchlistName"}
									helperText={
										errorContext === "updatedWatchlistName" && error
											? getTranslation(`global.sharedComponents.LPRWatchlistPanel.error.${error}`)
											: ""
									}
									onChange={event => {
										setUpdatedName(event.target.value);
										if (errorContext === "updatedWatchlistName") {
											setErrorContext("");
											setError("");
										}
									}}
									InputProps={styles.standardTextFieldInputProps}
									InputLabelProps={{
										sx: styles.textFieldInputLabelProps
									}}
									sx={styles.watchlistTextFieldSx} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-text-field-3"
								/>
								<TextField
									fullWidth
									variant="standard"
									label={getTranslation("global.sharedComponents.LPRWatchlistPanel.description")}
									value={updatedDesc}
									onChange={event => {
										setUpdatedDesc(event.target.value);
									}}
									InputProps={styles.standardTextFieldInputProps}
									InputLabelProps={{
										sx: styles.textFieldInputLabelProps
									}}
									sx={styles.watchlistTextFieldSx} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-text-field-4"
								/>
							</Box>
						</DialogContent>
						<DialogActions data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-dialog-actions">
							<Button
								style={styles.dialogButton}
								color="primary"
								onClick={() => {
									dispatch(closeDialog("editWatchlist"));
									setCurrentWatchlist(null);
								}} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-button-3"
							>
								<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.cancel" />
							</Button>
							<Button
								style={styles.dialogButton}
								variant="contained"
								color="primary"
								sx={styles.saveButtonSx}
								onClick={() => updateWatchlist(currentWatchlist.id, updatedName, updatedDesc)}
								disabled={
									!updatedName.trim() ||
									(updatedName?.trim() === currentWatchlist?.name &&
										updatedDesc?.trim() === currentWatchlist?.description)
								} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-button-4"
							>
								<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.save" />
							</Button>
						</DialogActions>
					</Dialog>
					<Dialog
						open={dialog === "deleteWatchlist"}
						PaperProps={{
							sx: styles.deleteDialogPaperSx
						}} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-dialog-2"
					>
						<DialogContent data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-dialog-content-2">
							<Typography style={styles.whiteTypography}>
								{canDeleteWatchlist
									? getTranslation(
											"global.sharedComponents.LPRWatchlistPanel.dialog.deleteWatchlistText",
											currentWatchlist?.name
										)
									: getTranslation(
											"global.sharedComponents.LPRWatchlistPanel.dialog.cannotDeleteWatchlist"
										)}
							</Typography>
						</DialogContent>
						<DialogActions data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-dialog-actions-2">
							{canDeleteWatchlist && (
								<Button
									style={styles.dialogButton}
									variant="contained"
									sx={styles.deleteConfirmButtonSx}
									onClick={() => deleteWatchlist(currentWatchlist.id)} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-button-5"
								>
									<Translate value="global.sharedComponents.LPRWatchlistPanel.dialog.confirm" />
								</Button>
							)}
							<Button
								style={styles.deleteWatchlistBackButton(canDeleteWatchlist)}
								color="primary"
								onClick={() => {
									dispatch(closeDialog("deleteWatchlist"));
									setCurrentWatchlist(null);
								}} data-testid="orion-components-dock-lprwatchlist-panel-watchlists-view-button-6"
							>
								{canDeleteWatchlist
									? getTranslation("global.sharedComponents.LPRWatchlistPanel.dialog.cancel")
									: getTranslation("global.sharedComponents.LPRWatchlistPanel.dialog.back")}
							</Button>
						</DialogActions>
					</Dialog>
				</>
			)}
		</>
	);
};

export default WatchlistsView;
